import { t } from './core'
import type { TranslationKey } from './locales/en'

const ERROR_CODE_KEYS = {
  FILE_PATH_INVALID: 'error.file_access.invalidPath',
  FILE_PATH_OUTSIDE_ALLOWED: 'error.file_access.outsideAllowedDirectories',
  FILE_SENSITIVE_ACCESS_DENIED: 'error.file_access.sensitiveFile',
  FILE_ROOT_UNAVAILABLE: 'error.file_access.noWorkspaceRoots',
  FILE_NOT_DIRECTORY: 'error.file_access.notDirectory',
  FILE_OPEN_FAILED: 'error.file_access.openFailed',
  FILE_READ_FAILED: 'error.file_access.readFailed',
  FILE_STAT_FAILED: 'error.file_access.statFailed',
  FILE_WRONG_CLIENT: 'error.file_access.wrongClient',
} as const satisfies Record<string, TranslationKey>

function readErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code.trim().toUpperCase() : undefined
}

function readErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  return error == null ? '' : String(error)
}

/** Convert known backend/runtime errors into localized user-facing messages. */
export function localizeErrorMessage(error: unknown, fallbackKey: TranslationKey = 'error.unknown_error.message'): string {
  const raw = readErrorMessage(error)
  const message = raw.trim()
  if (!message) return t(fallbackKey)

  const normalized = message.toLowerCase()

  // Prefer stable RPC error codes when present. Keep the legacy text matching
  // below for older remote servers that only return HANDLER_ERROR.
  const code = readErrorCode(error)
  if (code === 'FILE_PATH_INVALID' && normalized.includes('home-relative')) {
    return t('error.file_access.homeRelativePath')
  }
  const codedKey = code ? ERROR_CODE_KEYS[code as keyof typeof ERROR_CODE_KEYS] : undefined
  if (codedKey) return t(codedKey)

  if (normalized.includes('outside allowed directories')) return t('error.file_access.outsideAllowedDirectories')
  if (normalized.includes('no workspace file roots') || normalized.includes('no authorized session roots')) return t('error.file_access.noWorkspaceRoots')
  if (normalized.includes('sensitive file')) return t('error.file_access.sensitiveFile')
  if (normalized.includes('home-relative paths are not allowed')) return t('error.file_access.homeRelativePath')
  if (normalized.includes('only absolute file paths are allowed')) return t('error.file_access.invalidPath')
  if (normalized.includes('path is not a directory')) return t('error.file_access.notDirectory')
  if (normalized.includes('download session belongs to a different client')) return t('error.file_access.wrongClient')
  if (normalized.startsWith('failed to open file')) return t('error.file_access.openFailed')
  if (normalized.includes('failed to read file') || normalized.includes('unable to read file')) return t('error.file_access.readFailed')
  return message
}
